import React, { useState } from "react";
import { Ban, X, Star, ScrollText, Map, Swords, Landmark, Trophy, Compass, ChevronDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ALL = [
  { key: "skilling", label: "Skilling", Icon: Star },
  { key: "quests", label: "Quests", Icon: ScrollText },
  { key: "diaries", label: "Diaries", Icon: Map },
  { key: "combat", label: "Combat", Icon: Swords },
  { key: "raids", label: "Raids", Icon: Landmark },
  { key: "collection", label: "Collection", Icon: Trophy },
  { key: "clues", label: "Clues", Icon: Compass },
];
const F2P_KEYS = new Set(["skilling", "quests", "combat", "clues"]);
const BLOCK_COST = 100;
const MAX_BLOCKS = 3;

export default function BlockTaskTypes({ profileId, blocked = [], totalPoints = 0, f2pOnly = false, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirmCat, setConfirmCat] = useState(null); // {key,label} pending block confirmation
  const cats = ALL.filter((c) => (f2pOnly ? F2P_KEYS.has(c.key) : true));
  const used = blocked.length;

  const doBlock = async (key) => {
    setBusy(key);
    setConfirmCat(null);
    try {
      const { data } = await api.post(`/profiles/${profileId}/block-category`, { category: key });
      toast.success(data.message);
      onChange?.(data.blocked_categories);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const unblock = async (key, label) => {
    setBusy(key);
    try {
      const { data } = await api.post(`/profiles/${profileId}/unblock-category`, { category: key });
      toast(data.message, { icon: "🔓" });
      onChange?.(data.blocked_categories);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-4 border-2 border-black bg-[#241b10]" data-testid="block-task-types">
      <button
        data-testid="block-panel-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Ban size={16} className="text-[#FF9966] shrink-0" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
        <span className="osrs-shadow text-sm text-[#E6A519]">Block task types</span>
        <span className="osrs-num text-xs text-[#FFCF3F] ml-1" data-testid="block-count">
          {used}/{MAX_BLOCKS} · {BLOCK_COST} pts each
        </span>
        <ChevronDown size={16} className={`ml-auto text-[#7a6a45] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1" data-testid="block-panel-body">
          <p className="osrs-shadow text-[11px] text-[#b9a06a] mb-2 leading-snug">
            Never get a task type you hate. Blocking costs {BLOCK_COST} points (non-refundable) and applies to your
            next assignment. Unblocking is free.
          </p>
          <div className="flex flex-wrap gap-2">
            {cats.map(({ key, label, Icon }) => {
              const isBlocked = blocked.includes(key);
              const canBlock = !isBlocked && used < MAX_BLOCKS && totalPoints >= BLOCK_COST;
              return isBlocked ? (
                <button
                  key={key}
                  data-testid={`unblock-${key}`}
                  disabled={busy === key}
                  onClick={() => unblock(key, label)}
                  title="Unblock (free)"
                  className="inline-flex items-center gap-1 px-2 py-1 border border-black bg-[#4a1414] text-[#ff8f8f] osrs-num text-xs"
                >
                  <Icon size={12} /> {label} <X size={12} className="ml-0.5" />
                </button>
              ) : (
                <button
                  key={key}
                  data-testid={`block-${key}`}
                  disabled={busy === key || !canBlock}
                  onClick={() => setConfirmCat({ key, label })}
                  title={used >= MAX_BLOCKS ? "Block limit reached — unblock one first" : totalPoints < BLOCK_COST ? `Need ${BLOCK_COST} points` : `Block for ${BLOCK_COST} pts`}
                  className={`inline-flex items-center gap-1 px-2 py-1 border border-black osrs-num text-xs transition-colors ${
                    canBlock ? "bg-[#382d1a] text-[#E6A519] hover:text-[#FFCF3F]" : "bg-black/40 text-[#5a4a2a] cursor-not-allowed"
                  }`}
                >
                  <Icon size={12} /> {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {confirmCat && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="block-confirm-overlay"
          onClick={() => setConfirmCat(null)}
        >
          <div
            className="osrs-panel p-5 max-w-sm w-full text-center"
            data-testid="block-confirm-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 w-11 h-11 flex items-center justify-center border-2 border-black bg-[#4a1414]">
              <Ban size={22} className="text-[#ff8f8f]" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
            </div>
            <h3 className="osrs-title text-lg text-[#FFCF3F] mb-1">Block {confirmCat.label} tasks?</h3>
            <p className="osrs-shadow text-sm text-[#E6A519] leading-snug mb-1">
              This costs <span className="text-[#FFCF3F] osrs-num">{BLOCK_COST} points</span> and is
              <span className="text-[#ff8f8f]"> non-refundable</span>. You'll never be assigned {confirmCat.label} tasks
              until you unblock (which is free).
            </p>
            <p className="osrs-shadow text-xs text-[#b9a06a] mb-4">
              Balance after: <span className="osrs-num text-[#FFCF3F]">{totalPoints} → {totalPoints - BLOCK_COST}</span> pts
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="block-confirm-cancel"
                onClick={() => setConfirmCat(null)}
                className="osrs-btn px-5 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                data-testid="block-confirm-accept"
                disabled={busy === confirmCat.key}
                onClick={() => doBlock(confirmCat.key)}
                className="osrs-btn osrs-btn-red px-5 py-2 text-sm flex items-center gap-2"
              >
                <Ban size={14} /> {busy === confirmCat.key ? "Blocking..." : `Block (-${BLOCK_COST})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
